import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, Camera, MessageSquare, Wrench, ExternalLink } from 'lucide-react';
import { useAuth } from '../App';
import { PA_API, ACCOUNT_ROLES } from './portalAdmin/shared';
import CustomersTab from './portalAdmin/CustomersTab';
import PhotosTab from './portalAdmin/PhotosTab';
import MessagesTab from './portalAdmin/MessagesTab';
import RequestsTab from './portalAdmin/RequestsTab';

const TABS = [
  { id: 'customers', label: 'Customers & access', icon: Building2 },
  { id: 'photos', label: 'Photo approvals', icon: Camera, countKey: 'photos' },
  { id: 'messages', label: 'Messages', icon: MessageSquare, countKey: 'messages' },
  { id: 'requests', label: 'Service requests', icon: Wrench, countKey: 'requests' },
];

export default function PortalAdmin() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const tab = TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : 'customers';
  const [counts, setCounts] = useState({});
  const canManage = ACCOUNT_ROLES.includes(user?.role);

  const refreshCounts = useCallback(async () => {
    try { const r = await axios.get(`${PA_API}/unread-count`); setCounts(r.data); } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { refreshCounts(); const t = setInterval(refreshCounts, 60000); return () => clearInterval(t); }, [refreshCounts]);

  const setTab = (id) => { const p = new URLSearchParams(location.search); p.set('tab', id); if (id !== 'messages') p.delete('customer'); navigate(`/portal-admin?${p.toString()}`, { replace: true }); };

  return (
    <div className="space-y-4" data-testid="portal-admin">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Customer Portal</h1>
          <p className="text-sm text-slate-500 mt-0.5">Who can log in at <span className="font-mono text-slate-700">/portal</span>, what they see, and everything they send us.</p>
        </div>
        <a href="/portal/login" target="_blank" rel="noreferrer" className="px-3 py-2 rounded-xl bg-slate-900/5 text-slate-900 text-sm font-semibold flex items-center gap-1.5" data-testid="pa-open-portal"><ExternalLink className="w-4 h-4" /> Open the portal</a>
      </div>
      <div className="flex gap-1 bg-white p-1 rounded-xl border border-slate-200 overflow-x-auto">
        {TABS.map((t) => {
          const n = t.countKey ? counts[t.countKey] || 0 : 0;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`pa-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap flex-shrink-0 transition-all ${tab === t.id ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}>
              <t.icon className="w-4 h-4" /> {t.label}
              {n > 0 && <span className={`ml-1 text-[11px] px-1.5 py-0.5 rounded-full font-bold ${tab === t.id ? 'bg-slate-900/10' : 'bg-amber-500 text-white'}`} data-testid={`pa-count-${t.id}`}>{n}</span>}
            </button>);
        })}
      </div>
      {tab === 'customers' && <CustomersTab canManage={canManage} />}
      {tab === 'photos' && <PhotosTab onChanged={refreshCounts} />}
      {tab === 'messages' && <MessagesTab onChanged={refreshCounts} initialCustomer={params.get('customer')} />}
      {tab === 'requests' && <RequestsTab onChanged={refreshCounts} />}
    </div>
  );
}
